import { extendTheme, linkClasses } from '@mui/joy';

export type ExtendedColor = {
	solidBg: string;
	solidBg2: string;
	solidBg3: string;
	solidBoldBg: string;
	solidBorder: string;
	solidColor: string;
	outlinedColor: string;
	controlFill: string;
};

// You can put this to any file that's included in your tsconfig
declare module '@mui/joy/styles' {
	interface Palette {
		chaptersAnalysis: ExtendedColor;
		chaptersDonors: ExtendedColor;
		chaptersCandidates: ExtendedColor;
		chaptersOpportunities: ExtendedColor;
		categoryGenderMale: ExtendedColor;
		categoryGenderFemale: ExtendedColor;
		categoryPartyRepublican: ExtendedColor;
		categoryPartyDemocrat: ExtendedColor;
		categoryPartyIndependent: ExtendedColor;
		categoryPartyRepublicanWomen: ExtendedColor;
		categoryPartyDemocratWomen: ExtendedColor;
		categoryPartyIndependentWomen: ExtendedColor;
		categoryPartyRepublicanMen: ExtendedColor;
		categoryPartyDemocratMen: ExtendedColor;
		categoryPartyIndependentMen: ExtendedColor;
		categoryNoData: ExtendedColor;
		chartElementsBackground: {
			yAxisLabels: string;
			xAxisLabels: string;
			annotations: string;
			gridLines: string;
			baseLine: string;
			subTitle: string;
			title: string;
			type: string;
			background: string;
			bg: string;
		};
		controls: ExtendedColor;
	}
	interface TypographySystemOverrides {
		body1: true;
		body2: true;
		body3: true;
		chartTitle: true;
		subgroupTitle: true;
		label: true;
		chartSubtitle: true;
		chartFilter: true;
		annotation: true;
		caption: true;
	}
	interface BreakpointOverrides {
		smallLaptop: true;
		laptop: true;
	}
}

export const lightTheme = extendTheme({
	colorSchemes: {
		light: {
			palette: {
				neutral: {
					outlinedBg: '#F6F8FA',
					outlinedHoverBg: '#374151',
					outlinedActiveBg: '#374151',
					outlinedHoverColor: '#FFFFFF',
					outlinedActiveColor: '#FFFFFF'
				},
				chaptersAnalysis: {
					solidBg: '#7268AD',
					solidBg2: '#9A93C3',
					solidBg3: '#E3E1EF'
				},
				chaptersCandidates: {
					solidBg: '#255558',
					solidBg2: '#93ABAC',
					solidBg3: '#E9EEEE',
					solidBoldBg: '#163336',
					solidBorder: '#4AA9B0',
					solidColor: '#ffffff',
					outlinedColor: '#255558'
				},
				chaptersDonors: {
					solidBg: '#A6517A',
					solidBg2: '#C185A2',
					solidBg3: '#EEDDE5',
					solidBoldBg: '#A6517A',
					solidBorder: '#A6517A',
					solidColor: '#ffffff',
					outlinedColor: '#A6517A'
				},
				chaptersOpportunities: {
					solidBg: '#B39851',
					solidBg2: '#D7C9A3',
					solidBg3: '#EFE9DA',
					solidBoldBg: '#B39851',
					solidBorder: '#333333',
					solidColor: '#000000',
					outlinedColor: '#B39851'
				},
				categoryGenderMale: {
					solidBg: '#5C9474',
					solidColor: '#000000',
					outlinedColor: '#5C9474'
				},
				categoryGenderFemale: {
					solidBg: '#7268AD',
					solidColor: '#ffffff',
					outlinedColor: '#7268AD'
				},
				categoryPartyRepublican: {
					solidBg: '#CC1F36',
					solidColor: '#ffffff',
					outlinedColor: '#CC1F36'
				},
				categoryPartyDemocrat: {
					solidBg: '#1A458A',
					solidColor: '#ffffff',
					outlinedColor: '#1A458A'
				},
				categoryPartyIndependent: {
					solidBg: '#72757A',
					solidColor: '#ffffff',
					outlinedColor: '#72757A'
				},
				categoryPartyRepublicanWomen: {
					solidBg: '#8F1626',
					solidColor: '#ffffff',
					outlinedColor: '#8F1626'
				},
				categoryPartyDemocratWomen: {
					solidBg: '#123061',
					solidColor: '#ffffff',
					outlinedColor: '#123061'
				},
				categoryPartyIndependentWomen: {
					solidBg: '#484182',
					solidColor: '#ffffff',
					outlinedColor: '#484182'
				},
				categoryPartyRepublicanMen: {
					solidBg: '#DB6272',
					solidColor: '#000000',
					outlinedColor: '#DB6272'
				},
				categoryPartyDemocratMen: {
					solidBg: '#5F7DAD',
					solidColor: '#000000',
					outlinedColor: '#5F7DAD'
				},
				categoryPartyIndependentMen: {
					solidBg: '#8F83CB',
					solidColor: '#000000',
					outlinedColor: '#8F83CB'
				},
				categoryNoData: {
					solidBg: '#D1D5DB',
					solidColor: '#000000',
					outlinedColor: '#D1D5DB'
				},
				chartElementsBackground: {
					yAxisLabels: '#858585',
					xAxisLabels: '#5c5c5c',
					annotations: '#707070',
					gridLines: '#dadada',
					baseLine: '#333333',
					subTitle: '#4B5563',
					title: '#111827',
					type: '#111827',
					background: '#707070',
					bg: '#EFF5F8'
				},
				divider: '#000000',
				background: {
					surface: '#FAFAFA'
				},
				controls: {
					controlFill: '#374151'
				}
			}
		}
	},
	fontFamily: {
		display: 'Heebo, sans-serif', // applies to `h1`–`h4`
		body: 'Heebo, sans-serif' // applies to `title-*` and `body-*`
	},
	typography: {
		h1: {
			fontSize: '3.75rem',
			lineHeight: '3.75rem',
			fontWeight: 'bold',
			color: '#000000'
		},
		h2: {
			fontSize: '1.5rem',
			color: '#101827',
			lineHeight: '1.875rem'
		},
		h3: {
			textTransform: 'uppercase',
			fontSize: '1.125rem',
			color: '#374462',
			lineHeight: '1.875rem',
			fontWeight: 'normal'
		},
		//for analysis page
		h4: {
			fontSize: '2rem',
			color: '#333333',
			lineHeight: '2.5rem',
			fontWeight: '500',
			letterSpacing: '0px'
		},
		body1: {
			fontSize: '1.125rem',
			lineHeight: '1.875rem',
			fontFamily: 'Heebo, sans-serif',
			color: '#111827'
		},
		body2: {
			fontSize: '1rem',
			lineHeight: '1.875rem',
			fontFamily: 'Heebo, sans-serif',
			color: '#111827'
		},
		//for analysis page
		body3: {
			fontSize: '1.5625rem',
			lineHeight: '2.25rem',
			fontFamily: 'Heebo, sans-serif',
			color: '#333333'
		},
		chartTitle: {
			fontSize: '1.5625rem',
			lineHeight: '1.75rem',
			color: '#101827',
			fontWeight: 'normal',
			fontFamily: 'Heebo, sans-serif'
		},
		subgroupTitle: {
			fontSize: '1.5625rem',
			lineHeight: '2rem',
			fontFamily: 'Source Sans Pro, sans-serif',
			color: '#111827'
		},
		label: {
			fontSize: '1.25rem',
			lineHeight: '1.5625rem',
			fontFamily: 'Source Sans Pro, sans-serif',
			color: '#111827'
		},
		chartSubtitle: {
			fontFamily: 'Heebo, sans-serif',
			fontSize: '1.125rem',
			lineHeight: '1.5625rem',
			color: '#4B5563'
		},
		chartFilter: {
			fontSize: '1.125rem',
			lineHeight: '1.5625rem',
			fontFamily: 'Source Sans Pro, sans-serif',
			color: '#111827'
		},
		annotation: {
			fontSize: '1rem',
			lineHeight: '1.5625rem',
			color: '#707070'
		},
		caption: {
			fontSize: '1rem',
			fontFamily: 'Source Sans Pro, sans-serif',
			color: '#4B5563'
		}
	},
	components: {
		JoyLink: {
			styleOverrides: {
				[`&.${linkClasses.colorPrimary}`]: {
					color: 'red',
					':hover': { color: '#111827' }
				}
			}
		}
	},
	breakpoints: {
		values: {
			xs: 0,
			sm: 600,
			md: 900,
			lg: 1240,
			xl: 1536,
			smallLaptop: 1024,
			laptop: 1440
		}
	}
});
